import { isLocalDev } from "../common/env.ts";
import { FullLocale } from "../common/utils/locale.ts";

function buildBookshelfURL(path: string) {
  if (isLocalDev()) {
    return `https://bookshelf-dev.vitalsource.com${path}`;
  }
  return `https://${window.location.host}${path}`;
}

// basic reader URL, does not handle activation or return url
export function getReaderURL(vbid: string): string {
  return buildBookshelfURL(`/reader/books/${vbid}`);
}

export function getCheckoutURL(
  path: string,
  params: { redirect_url: string; locale: FullLocale }
): string {
  const searchParams = new URLSearchParams(params);
  return buildBookshelfURL(`/checkout${path}`) + "?" + searchParams.toString();
}

interface ReaderNavUrlOptions {
  // home return url passed to reader so it comes back to the same place in home
  // when reader back button is pressed, default true
  withReturn?: boolean;
  // if a route needs added return details (e.g. which content search was open )
  returnDetail?: string;
  // deep link to a page or cfi
  deepLink?: string;
  // if the vbid is an explore asset, activate first?
  needsActivation?: boolean;
}

// a reader URL with a view to navigation, option to handle activation and
// option for reader to return to the part of home that launched it and deepLink provision
export function getReaderNavURL(
  vbid: string,
  {
    withReturn = true,
    returnDetail,
    deepLink,
    needsActivation = false,
  }: ReaderNavUrlOptions = {}
) {
  const base = needsActivation ? `/activate/${vbid}` : getReaderURL(vbid);
  let returnQuery = "";
  if (withReturn) {
    const location = new URL(window.location.href);
    if (returnDetail) {
      location.searchParams.set("library_return_detail", returnDetail);
    }
    returnQuery = "?library_return_url=" + encodeURIComponent(location.href);
  }
  return `${base}${deepLink ?? ""}${returnQuery}`;
}

// a reader URL but in router Link format
export function getReaderNavLink(vbid: string, opts: ReaderNavUrlOptions = {}) {
  const url = getReaderNavURL(vbid, opts);
  return { ...(opts.needsActivation ? { to: url } : { href: url }) };
}

export enum DeepLinkType {
  Cfi,
  PageId,
}

// get a deep link into a reader page or cfi
export function getReaderDeepLink(link: string, type: DeepLinkType) {
  // pbk page id
  if (type === DeepLinkType.PageId) {
    return "/pageid/" + link;
  }
  // epub cfi
  if (link.startsWith("/")) {
    link = link.substring(1);
  }

  return "/epubcfi/" + link;
}

export function getExploreURL({
  subpath,
  searchParams,
}: {
  subpath?: string;
  searchParams?: URLSearchParams;
} = {}) {
  let fixedSubpath = subpath ?? "";
  if (fixedSubpath.length > 1 && !fixedSubpath.startsWith("/")) {
    fixedSubpath = `/${fixedSubpath}`;
  }
  const fixedParams = new URLSearchParams(searchParams?.toString());
  fixedParams.set("native", "true");
  fixedParams.set("platform", "web");

  return buildBookshelfURL(`/explore${fixedSubpath}?${fixedParams.toString()}`);
}

export function getSystemErrorURL(params: { locale?: string } = {}) {
  const url = buildBookshelfURL("/error/system-error-space");
  const queryString = new URLSearchParams(params).toString();
  if (queryString) {
    return url + "?" + queryString;
  }
  return url;
}

export function getNotFoundErrorURL(
  params: {
    locale?: string;
    theme?: string;
    missingURL?: string;
    storeLink?: string;
  } = {}
) {
  const url = buildBookshelfURL("/error/not-found");
  const queryString = new URLSearchParams(params).toString();
  if (queryString) {
    return url + "?" + queryString;
  }
  return url;
}

export function rebuildHashedRoute(
  location: Pick<Location, "search" | "hash" | "pathname">
): string | void {
  const search = location.search;
  const hash = location.hash;
  if (hash) {
    // if there is a hash followed by a slash, remove the hash and append the slash to the path
    if (hash.startsWith("#/")) {
      const pathname = location.pathname;
      const hashSearch = hash.includes("?")
        ? hash.substring(hash.indexOf("?") + 1)
        : "";
      const hashPath = hash.slice(
        1,
        hash.includes("?") ? hash.indexOf("?") : undefined
      );

      return `${pathname}${hashPath}${
        search ? `${search}&${hashSearch}` : hashSearch ? `?${hashSearch}` : ""
      }`
        .replace(/\/+/g, "/")
        .replace(/^\/home\//, "/");
    }
  }
}
